<template>
  <div id="desktop-navigation" class="desktop-menu-container relative ml-auto">
    <nav class="relative z-10 flex flex-row gap-8 place-items-center ml-auto pl-4 pr-8 py-2 w-full text-black bg-white rounded-bl-lg">
      <div class="flex flex-row place-items-center xl:mr-10">
        <div>
          <AtomLink :url="homeUrl" title="Home" class="block">
            <AtomBrandLogoDesktop class="logo-height" />
          </AtomLink>
        </div>
      </div>
      <div class="flex flex-1 flex-col">
        <div class="pb-2 pl-2 pt-1 border-b">
          <template v-if="secondaryMenuTitle">
            <div class="flex flex-row items-center w-full">
              <div>
                <AtomIcon icon="app-location-pin" :class="'shrink-0 w-5 h-5 text-app-green inline-block'" />
                {{ secondaryMenuTitle }}
              </div>
              <AtomLink url="/" :title="t_('home-label')" class="ml-auto">
                <div class="flex items-center px-2 py-1 text-xs bg-app-green-200 rounded-md">
                  <AtomIcon icon="app-log-out" :class="'shrink-0 w-3 h-3 inline-block mr-2 text-app-green'" />
                  {{ t_('home-label') }}
                </div>
              </AtomLink>
            </div>
          </template>
          <ul v-else-if="service.length" id="desktop-navigation-service-menu" class="grid gap-8 grid-flow-col auto-cols-max xl:gap-12">
            <li v-for="(item, key) in service" :key="key">
              <AtomLink :url="item.url" :title="item.title" class="flex items-center text-sm border-b-2 hover:border-app-green border-transparent cursor-pointer">
                <AtomIcon v-if="item.icon" :icon="item.icon.slug" class="mr-2 w-4" />
                <span>{{ item.title }}</span>
              </AtomLink>
            </li>
          </ul>
        </div>
        <div v-if="main.length" class="pl-2 pt-1">
          <ul id="desktop-navigation-main-menu" class="grid gap-6 xl:gap-8 grid-flow-col mt-2 leading-none auto-cols-max xl:gap-12">
            <li
              v-for="(item, key) in main"
              :key="key"
              class="relative flex items-center pb-1 font-bold border-b-2 hover:border-app-green border-transparent cursor-pointer uppercase"
              @click="addNextChildren(item)"
            >
              <AtomLink v-if="!item.children.length" :url="item.url" class="mt-2px">{{ item.title }}</AtomLink>
              <button v-else :id="`button--${item.id}`" class="font-bold uppercase font-body flex items-center">
                <span class="mr-2">{{ item.title }}</span>
                <AtomIconArrowDown v-if="item.children.length" class="w-3 h-3 transform transition duration-200" :class="{ 'rotate-180': menuTracker === item.title }" />
              </button>

              <ul v-show="simpleMenu && menuTracker === item.title" class="simple-menu absolute p-6 pb-8 bg-white border-b-2 border-solid border-app-green rounded-b-2xl">
                <template v-for="(simpleMenuItem, simpleMenuItemKey) in item.children">
                  <li
                    v-if="simpleMenuItem.url !== null && simpleMenuItem.url !== undefined"
                    :key="simpleMenuItemKey"
                    :class="{ 'mb-6': simpleMenuItemKey + 1 < item.children.length }"
                    class="leading-5"
                  >
                    <MoleculeDesktopMenuItem :title="simpleMenuItem.title" :url="simpleMenuItem.url" />
                  </li>
                </template>
              </ul>
            </li>
          </ul>
        </div>
      </div>
      <div class="flex ml-auto">
        <AtomButton
          v-if="cta && cta.url"
          id="desktop-navigation-btn-cta-donation"
          :url="cta.url"
          button-type="primary"
          :full-width="false"
          class="flex shrink-0 place-items-center"
        >
          <div class="flex" :class="{ 'mr-4': cta.icon }">{{ cta.title }}</div>
          <AtomIcon v-if="cta.icon" :icon="cta.icon.slug" class="text-black w-6 h-6 flex" />
        </AtomButton>

        <template v-if="domain === 'ikZoekBaas'">
          <AtomButton id="desktop-navigation-btn-login" button-type="secondary" class="ml-3" :url="userStore.hasUser ? { name: 'account-detail' } : { name: 'account-login' }">
            <AtomIconPerson class="flex mr-2 w-4 text-white" />
            {{ t_(userStore.hasUser ? 'account-label' : 'login-label') }}
          </AtomButton>
        </template>

        <AtomButton v-if="domain === 'default'" id="desktop-navigation-btn-search" button-type="secondary" class="ml-3" @click.prevent="desktopSearch">
          <span class="app-clip">{{ t_('search') }}</span>
          <AtomIconSearch class="flex w-4 text-white" />
        </AtomButton>
      </div>
    </nav>

    <div
      class="submenu-folded js-auto-height-flow-menu absolute z-0 right-0 ml-auto pt-6 px-5 w-full bg-white border-b-2 border-app-green rounded-bl-lg overflow-hidden"
      :class="{ 'border-app-lime': !secondaryMenuTitle && !ikZoekBaas }"
    >
      <template v-if="!simpleMenu">
        <ul v-for="(item, key) in main" v-show="currentMenuId === item.id && item.children.length" :id="`children--${item.id}`" :key="key" class="flex pb-4 pt-4 max-w-screen-xl">
          <template v-for="(child, childKey) in item.children" :key="childKey">
            <li v-if="child.url !== null && child.url !== undefined" class="flex-1 pl-8" :class="{ 'border-r': childKey < item.children.length - 1 }">
              <span class="font-bold uppercase">
                <MoleculeDesktopMenuItem :title="child.title" :url="child.url" />
              </span>
              <ul v-if="child.children">
                <template v-for="(innerItem, innerItemKey) in child.children">
                  <li v-if="innerItem.url !== null && innerItem.url !== undefined" :key="innerItemKey">
                    <MoleculeDesktopMenuItem :title="innerItem.title" :url="innerItem.url" />
                  </li>
                </template>
              </ul>
            </li>
          </template>
        </ul>
      </template>
    </div>

    <div
      v-if="desktopSearchStatus"
      id="desktop-navigation-search-container"
      class="desktop-search-container absolute z-50 right-0 px-16 py-6 w-full max-w-xl bg-white border-b-2 border-solid border-app-lime rounded-bl-2xl rounded-br-2xl shadow-inner shadow-md"
    >
      <div class="relative">
        <input
          id="desktop-navigation-search-field"
          v-model="desktopSearchInput"
          type="text"
          :placeholder="t_('search-placeholder')"
          class="search-markup pl-4 pr-20 py-2 w-full bg-gray-200 rounded-3xl"
          @keyup.enter="emitSearch"
        />
        <button class="desktop-search-icon absolute" @click="emitSearch">
          <AtomIconSearch class="mr-2 w-4 h-4" />
        </button>
      </div>
      <button class="absolute z-50 right-0 top-0 px-3 py-3 rounded-bl-2xl cursor-pointer" @click="desktopSearch">
        <AtomIconX class="w-3 h-3" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { getHomeUrl, setupSiteConfig } from '~/composables/craft'
import { setupTranslationPrefixer } from '~/composables/util'
import { useNavigationStore } from '~/store/navigation'
import { useUserStore } from '~/store/user'

const navigationStore = useNavigationStore()
const userStore = useUserStore()
const { $autoHeightFlow } = useNuxtApp()
const localePath = useLocalePath()
const { domain } = setupSiteConfig()
const route = useRoute()
const router = useRouter()
const menuTracker = ref('')
const currentMenu = ref('')
const { url: homeUrl, simpleMenu, ikZoekBaas } = getHomeUrl(route)

const currentMenuId = ref(null)
const desktopSearchStatus = ref(false)
const desktopSearchInput = ref('')
const desktopSearch = () => {
  desktopSearchStatus.value = !desktopSearchStatus.value
}
const emitSearch = () => {
  desktopSearchStatus.value = false
  router.push(localePath({ path: '/zoeken', query: { search: desktopSearchInput.value } }))
}

const addNextChildren = (item: any) => {
  if (item.children.length) {
    if (menuTracker.value === item.title) {
      menuTracker.value = ''
    } else {
      menuTracker.value = item.title
    }
    toggle(item)
  }
  if (currentMenuId.value === item.id) {
    currentMenuId.value = null
  } else {
    currentMenuId.value = item.id
    const parentElement = document.getElementById(`button--${currentMenuId.value}`)
    const itemLinks = document.getElementById(`children--${currentMenuId.value}`)?.querySelectorAll('a') || []
    if (parentElement && itemLinks.length > 0) {
      setTimeout(() => {
        itemLinks[0].focus()
        document.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.shiftKey && event.key === 'Tab') {
            const focusedElement = document.activeElement
            if (focusedElement === itemLinks[0]) {
              event.preventDefault()
              parentElement.focus()
            }
          }
        })
      }, 0)
    }
  }
}

const toggle = (menu: any) => {
  const dom = document.querySelector('.js-auto-height-flow-menu')
  if (currentMenu.value.title !== menu.title && dom!.style.maxHeight !== '0' && dom!.style.maxHeight !== '') {
    $autoHeightFlow(dom)
    currentMenu.value = menu
    $autoHeightFlow(dom!)
    return
  }
  currentMenu.value = menu
  $autoHeightFlow(dom)
}

const { main, service, secondaryMenuTitle, cta } = storeToRefs(navigationStore)

const { t_ } = setupTranslationPrefixer('DesktopMenu', domain)
</script>

<style lang="scss" scoped>
.submenu-folded {
  top: 66px;
  max-height: 0;
}

.desktop-menu-container {
  @screen xl {
    width: calc(((100vw - theme('screens.xl')) / 2) + theme('screens.xl'));
  }

  @screen 2xl {
    width: calc(((100vw - theme('screens.2xl')) / 2) + theme('screens.2xl'));
  }
}

.simple-menu {
  top: 33px;
  left: -14px;
  min-width: 285px;
}

.logo-height {
  height: 60px;
}

.search-markup {
  @apply border border-2 border-transparent;

  &:focus {
    outline: none;
    @apply border border-2 border-app-lime;
  }
}

.desktop-search-container {
  margin-top: 92px;
}

.desktop-search-icon {
  top: calc(50% - 8px);
  right: 12px;
}
</style>
